import React, { FC, FocusEvent, ReactNode } from "react";
import { _throw } from "shared-ts-utils-core";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { PropertyValueType } from "./PropertyValueType";
import { InputBaseProps } from "@mui/material/InputBase";
import { resolvePropertyValueTypeFromValue } from "./resolvePropertyValueTypeFromValue";
import { RemoveButton } from "./RemoveButton";
import { PropertyEditBaseProps } from "./PropertyEditBaseProps";
import { useSxMerge } from "shared-ts-mui";
import { usePropertyEditBase } from "./usePropertyEditBase";
import { UnitSelect } from "./UnitSelect";

export interface PropertyEditStringProps extends PropertyEditBaseProps {
  multiline?: boolean;
  type?: PropertyValueType;
  nullable?: boolean;
  onBlur?: (event: FocusEvent) => void;
  unit?: string;
  units?: Array<string>;
  onUnitChange?: (value: string) => void;
  autoFocus?: boolean;
  autoComplete?: string;
  helperText?: ReactNode;
  error?: boolean;
  inputProps?: InputBaseProps["inputProps"];
  inputRef?: React.Ref<HTMLInputElement>;
}

export const PropertyEditString: FC<PropertyEditStringProps> = (props) => {
  const { removable, handleRemove } = usePropertyEditBase(props);
  const inputType = resolveHtmlInputType(props.type ?? "string");

  const sx = useSxMerge(
    {
      width: props.fullWidth !== true ? "25ch" : undefined,
      ".MuiInputBase-root": {
        pr: removable ? 1 : undefined,
      },
    },
    props.sx
  );
  const handleTextFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const type =
      props.type != null
        ? props.type
        : resolvePropertyValueTypeFromValue(props.value);
    const typedValue = typeValue(event.target.value, type, props.nullable);
    props.onChange(
      typedValue,
      props.name ?? _throw(new Error("name cannot be null"))
    );
  };

  const handleBlur = (event: FocusEvent) => {
    props.onBlur?.(event);
  };
  const handleUnitChange = (value: string) => {
    props.onUnitChange?.(value);
  };

  return (
    <TextField
      className={"Property"}
      autoFocus={props.autoFocus}
      autoComplete={props.autoComplete}
      label={props.label}
      name={props.name ?? _throw(new Error("name cannot be null"))}
      value={props.value ?? ""}
      size={props.size}
      type={inputType}
      multiline={props.multiline}
      onChange={handleTextFieldChange}
      onBlur={handleBlur}
      fullWidth={props.fullWidth ?? false}
      inputRef={props.inputRef}
      helperText={props.helperText}
      error={props.error}
      sx={sx}
      slotProps={{
        input: {
          endAdornment:
            props.unit || props.info || removable ? (
              <InputAdornment position="end">
                {props.unit && props.units === undefined
                  ? props.unit
                  : undefined}
                {props.unit && props.units !== undefined ? (
                  <UnitSelect
                    selected={props.unit}
                    options={props.units}
                    onChange={handleUnitChange}
                    tabbable={false}
                  />
                ) : undefined}
                {props.info && (
                  <Tooltip title={props.info}>
                    <IconButton size={"small"}>
                      <HelpOutlineRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {removable && <RemoveButton onRemove={handleRemove} />}
              </InputAdornment>
            ) : undefined,
        },

        htmlInput: props.inputProps,
      }}
    />
  );
};

const resolveHtmlInputType = (
  type: PropertyValueType
): React.InputHTMLAttributes<unknown>["type"] | undefined => {
  if (type === "number") {
    return "number";
  } else if (type === "password") {
    return "password";
  } else {
    return "text";
  }
};

const typeValue = (
  valueAsString: string,
  type: PropertyValueType,
  nullable: boolean | undefined
): string | number | null => {
  if (type === "number") {
    if (nullable !== true || valueAsString !== "") return +valueAsString;
    return null;
  } else {
    return valueAsString;
  }
};
